/**
 * Workflow AI Suggestions Component
 *
 * Provides RAG-powered suggestions for workflow steps:
 * - Suggests next steps based on current workflow context
 * - Recommends similar workflows from the knowledge base
 * - Suggests step configurations based on patterns
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain,
  Sparkles,
  Plus,
  ChevronRight,
  Zap,
  RefreshCw,
  Lightbulb,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { WorkflowStep, StepType } from '@/services/api/workflows';
import { useAppTranslation } from '@/contexts/TranslationContext';

// Types
interface StepSuggestion {
  step_type: StepType;
  step_name: string;
  description: string;
  step_config: Record<string, any>;
  confidence: number;
  reasoning: string;
}

interface SimilarWorkflow {
  id: string;
  name: string;
  description?: string;
  step_count: number;
  relevance_score: number;
  trigger_type?: string;
}

interface WorkflowPattern {
  pattern_name: string;
  description: string;
  step_sequence: string[];
  usage_count: number;
}

interface WorkflowAISuggestionsProps {
  templateId: number;
  templateName: string;
  currentSteps: WorkflowStep[];
  triggerType?: string;
  onAddSuggestedStep?: (suggestion: StepSuggestion) => void;
  onViewSimilarWorkflow?: (workflowId: string) => void;
  className?: string;
}

const WorkflowAISuggestions: React.FC<WorkflowAISuggestionsProps> = ({
  templateId,
  templateName,
  currentSteps,
  triggerType,
  onAddSuggestedStep,
  onViewSimilarWorkflow,
  className = ''
}) => {
  const { t } = useAppTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stepSuggestions, setStepSuggestions] = useState<StepSuggestion[]>([]);
  const [similarWorkflows, setSimilarWorkflows] = useState<SimilarWorkflow[]>([]);
  const [patterns, setPatterns] = useState<WorkflowPattern[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [ragAvailable, setRagAvailable] = useState(false);

  // Check RAG availability
  useEffect(() => {
    const checkRAG = async () => {
      try {
        const response = await apiClient.get('/api/rag/config');
        const config = response.data;
        setRagAvailable(config?.rag_enabled && config?.features?.workflow_assist);
      } catch {
        setRagAvailable(false);
      }
    };
    checkRAG();
  }, []);

  // Fetch AI suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!ragAvailable) return;

    setIsLoading(true);
    setError(null);

    try {
      // Build context from current steps
      const context = {
        template_name: templateName,
        trigger_type: triggerType,
        current_steps: currentSteps.map(s => ({
          order: s.step_order,
          type: s.step_type,
          name: s.step_name,
        })),
        step_count: currentSteps.length,
      };

      // Call RAG search for similar workflows
      const searchPromise = apiClient.post('/api/rag/search', {
        query: `workflow steps for ${templateName} ${triggerType || ''} ${currentSteps.map(s => s.step_type).join(' ')}`,
        top_k: 5,
        document_types: ['workflow'],
        include_metadata: true,
      });

      // Call RAG query for step suggestions
      const queryPromise = apiClient.post('/api/rag/query', {
        query: `What steps should follow in a workflow with: ${currentSteps.map(s => s.step_name).join(', ')}? The workflow is for: ${templateName}`,
        generate_response: true,
        include_sources: true,
        top_k: 3,
      });

      const [searchRes, queryRes] = await Promise.allSettled([searchPromise, queryPromise]);

      // Process similar workflows
      if (searchRes.status === 'fulfilled') {
        const matches = searchRes.value.data?.results || searchRes.value.data?.matches || [];
        setSimilarWorkflows(matches.slice(0, 3).map((m: any) => ({
          id: m.metadata?.source_entity_id || m.document_id,
          name: m.title || m.metadata?.title || 'Workflow',
          description: m.content_preview,
          step_count: m.metadata?.step_count || 0,
          relevance_score: m.score || 0,
          trigger_type: m.metadata?.trigger_type,
        })));
      }

      // Process query response for step suggestions
      if (queryRes.status === 'fulfilled') {
        // Parse AI suggestions from response
        const data = queryRes.value.data;
        // For demo, generate suggestions based on context
        const suggestions = generateContextualSuggestions(currentSteps, templateName, triggerType);
        setStepSuggestions(suggestions);
      } else {
        // Fall back to contextual suggestions
        setStepSuggestions(generateContextualSuggestions(currentSteps, templateName, triggerType));
      }

      // Set common patterns
      setPatterns(getCommonPatterns(triggerType));

    } catch (err: any) {
      console.error('Error fetching workflow suggestions:', err);
      setError(err.response?.data?.detail || 'Failed to load AI suggestions');
      // Provide fallback suggestions
      setStepSuggestions(generateContextualSuggestions(currentSteps, templateName, triggerType));
      setPatterns(getCommonPatterns(triggerType));
    } finally {
      setIsLoading(false);
    }
  }, [ragAvailable, templateName, triggerType, currentSteps]);

  // Auto-fetch on mount and when steps change
  useEffect(() => {
    if (ragAvailable) {
      fetchSuggestions();
    }
  }, [ragAvailable, currentSteps.length]);

  // Generate contextual suggestions based on current workflow state
  const generateContextualSuggestions = (
    steps: WorkflowStep[],
    name: string,
    trigger?: string
  ): StepSuggestion[] => {
    const lastStep = steps[steps.length - 1];
    const suggestions: StepSuggestion[] = [];

    // If no steps, suggest initial steps based on trigger type
    if (steps.length === 0) {
      if (trigger?.includes('enrollment')) {
        suggestions.push({
          step_type: 'form',
          step_name: 'Complete Intake Assessment',
          description: 'Gather initial member information and consent',
          step_config: { form_type: 'intake_assessment', blocks_workflow: true },
          confidence: 0.92,
          reasoning: 'Enrollment workflows typically start with intake forms',
        });
      }
      suggestions.push({
        step_type: 'communication',
        step_name: 'Send Welcome Message',
        description: 'Send SMS/email introduction to the member',
        step_config: { channel: 'sms', expects_response: false },
        confidence: 0.85,
        reasoning: 'Initial contact establishes engagement',
      });
    } else if (lastStep) {
      // Suggest next steps based on last step type
      switch (lastStep.step_type) {
        case 'form':
          suggestions.push({
            step_type: 'approval',
            step_name: 'Review Completed Form',
            description: 'Supervisor reviews the submitted form',
            step_config: { approver_role: 'supervisor' },
            confidence: 0.88,
            reasoning: 'Forms typically require review before proceeding',
          });
          break;
        case 'communication':
          if (lastStep.step_config?.expects_response) {
            suggestions.push({
              step_type: 'wait_for_response',
              step_name: 'Wait for Member Reply',
              description: 'Wait for member to respond to outreach',
              step_config: { timeout_hours: 24, timeout_action: 'escalate' },
              confidence: 0.91,
              reasoning: 'Communication expecting response needs wait step',
            });
          } else {
            suggestions.push({
              step_type: 'task',
              step_name: 'Schedule Follow-up',
              description: 'Schedule next contact with member',
              step_config: { description: 'Review communication outcome and schedule follow-up' },
              confidence: 0.82,
              reasoning: 'Follow up ensures engagement continues',
            });
          }
          break;
        case 'approval':
          suggestions.push({
            step_type: 'notification',
            step_name: 'Notify Approval Outcome',
            description: 'Notify relevant parties of the approval decision',
            step_config: { recipient_type: 'assignee' },
            confidence: 0.86,
            reasoning: 'Stakeholders should be notified of approval outcomes',
          });
          break;
        case 'wait_for_response':
          suggestions.push({
            step_type: 'condition',
            step_name: 'Check Response Type',
            description: 'Branch based on member response',
            step_config: { condition_type: 'response_received' },
            confidence: 0.89,
            reasoning: 'Different responses may require different actions',
          });
          break;
        default:
          suggestions.push({
            step_type: 'task',
            step_name: 'Complete Next Action',
            description: 'Manual action to continue the workflow',
            step_config: {},
            confidence: 0.75,
            reasoning: 'Generic next step when pattern is unclear',
          });
      }
    }

    // Always suggest a completion notification if workflow has steps
    if (steps.length >= 3 && !steps.some(s => s.step_name.toLowerCase().includes('complet'))) {
      suggestions.push({
        step_type: 'notification',
        step_name: 'Workflow Completion Notice',
        description: 'Notify team when workflow is complete',
        step_config: { recipient_type: 'team' },
        confidence: 0.78,
        reasoning: 'Workflows benefit from completion notifications',
      });
    }

    return suggestions.slice(0, 3);
  };

  // Get common workflow patterns
  const getCommonPatterns = (trigger?: string): WorkflowPattern[] => {
    const patterns: WorkflowPattern[] = [
      {
        pattern_name: 'Enrollment & Onboarding',
        description: 'Standard member enrollment flow',
        step_sequence: ['Welcome SMS', 'Intake Form', 'Consent Collection', 'Care Plan Assignment'],
        usage_count: 156,
      },
      {
        pattern_name: 'Appointment Reminder',
        description: 'Multi-channel appointment reminders',
        step_sequence: ['7-Day Reminder', '1-Day Reminder', 'Same-Day Confirmation', 'No-Show Follow-up'],
        usage_count: 234,
      },
      {
        pattern_name: 'Assessment Follow-up',
        description: 'Post-assessment care coordination',
        step_sequence: ['Assessment Review', 'Care Plan Update', 'Provider Notification', 'Member Communication'],
        usage_count: 89,
      },
    ];

    if (trigger?.includes('referral')) {
      patterns.unshift({
        pattern_name: 'Referral Processing',
        description: 'Inbound referral handling workflow',
        step_sequence: ['Receive Referral', 'Eligibility Check', 'Provider Match', 'Member Outreach', 'Enrollment'],
        usage_count: 178,
      });
    }

    return patterns.slice(0, 3);
  };

  // Get step type icon
  const getStepTypeIcon = (type: StepType): string => {
    switch (type) {
      case 'task': return '📋';
      case 'notification': return '🔔';
      case 'communication': return '📧';
      case 'wait_for_response': return '⏳';
      case 'approval': return '✅';
      case 'condition': return '🔀';
      case 'form': return '📝';
      case 'billing': return '💰';
      default: return '📄';
    }
  };

  if (!ragAvailable) {
    return null;
  }

  return (
    <div className={`bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800 ${className}`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg">
            <Brain className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('workflows.aiSuggestions.title', 'AI Workflow Assistant')}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('workflows.aiSuggestions.subtitle', 'RAG-powered suggestions')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />}
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Suggested Next Steps */}
          {stepSuggestions.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  {t('workflows.aiSuggestions.suggestedNextSteps', 'Suggested Next Steps')}
                </h4>
                <button
                  onClick={fetchSuggestions}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  {t('workflows.aiSuggestions.refresh', 'Refresh')}
                </button>
              </div>
              <div className="space-y-2">
                {stepSuggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    className="group bg-white/70 dark:bg-gray-800/70 rounded-lg p-3 border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <span className="text-lg">{getStepTypeIcon(suggestion.step_type)}</span>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                            {suggestion.step_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {suggestion.description}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              suggestion.confidence > 0.85
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            }`}>
                              <Zap className="w-3 h-3 inline mr-0.5" />
                              {Math.round(suggestion.confidence * 100)}%
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {suggestion.reasoning}
                            </span>
                          </div>
                        </div>
                      </div>
                      {onAddSuggestedStep && (
                        <button
                          onClick={() => onAddSuggestedStep(suggestion)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-all"
                          title={t('workflows.aiSuggestions.addThisStep', 'Add this step')}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Similar Workflows */}
          {similarWorkflows.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                <ArrowRight className="w-4 h-4 text-blue-500" />
                {t('workflows.aiSuggestions.similarWorkflows', 'Similar Workflows')}
              </h4>
              <div className="flex flex-wrap gap-2">
                {similarWorkflows.map((workflow, idx) => (
                  <button
                    key={idx}
                    onClick={() => onViewSimilarWorkflow?.(workflow.id)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 text-sm transition-colors"
                  >
                    <span className="text-gray-700 dark:text-gray-300">{workflow.name}</span>
                    <span className="text-xs text-gray-400">{workflow.step_count} {t('workflows.aiSuggestions.steps', 'steps')}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Common Patterns */}
          {patterns.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-yellow-500" />
                {t('workflows.aiSuggestions.commonPatterns', 'Common Patterns')}
              </h4>
              <div className="space-y-2">
                {patterns.map((pattern, idx) => (
                  <div
                    key={idx}
                    className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                        {pattern.pattern_name}
                      </span>
                      <span className="text-xs text-gray-400">{pattern.usage_count} {t('workflows.aiSuggestions.uses', 'uses')}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {pattern.description}
                    </p>
                    <div className="flex items-center gap-1 flex-wrap">
                      {pattern.step_sequence.map((step, i) => (
                        <React.Fragment key={i}>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                            {step}
                          </span>
                          {i < pattern.step_sequence.length - 1 && (
                            <ChevronRight className="w-3 h-3 text-gray-400" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading state */}
          {isLoading && stepSuggestions.length === 0 && (
            <div className="text-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{t('workflows.aiSuggestions.analyzing', 'Analyzing workflow patterns...')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkflowAISuggestions;
